import React, { useState, useEffect, useRef } from 'react';
import { Mail, X, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Bulk email to selected clients (Feature 3). Requires SMTP configured in
// Settings — shows a clear notice instead of failing silently when it isn't.
export default function BulkEmailModal({ targets, defaultSubject, defaultBody, onClose }) {
  const [subject, setSubject] = useState(defaultSubject || '');
  const [body, setBody] = useState(defaultBody || '');
  const [sending, setSending] = useState(false);
  const [configured, setConfigured] = useState(null); // null = checking
  const subjectRef = useRef();

  useEffect(() => {
    api.get('/email/settings')
      .then(r => setConfigured(!!r.data.smtp_email))
      .catch(() => setConfigured(false));
  }, []);
  useEffect(() => { if (configured) subjectRef.current?.focus(); }, [configured]);

  const withEmail = targets.filter(t => t.email);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const r = await api.post('/email/bulk-send', {
        client_ids: [...new Set(withEmail.map(t => t.client_id || t.id))],
        subject, body,
      });
      toast.success(`Sent to ${r.data.sent} client${r.data.sent !== 1 ? 's' : ''}${r.data.skipped?.length ? ` (${r.data.skipped.length} skipped)` : ''}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <Mail size={20} className="text-blue-600" />
          <h3 className="font-semibold text-gray-900">Email — {withEmail.length} of {targets.length} client{targets.length !== 1 ? 's' : ''}</h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {configured === false ? (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-700 flex items-start gap-2">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Email not configured</p>
              <p className="mt-1">Set up SMTP in <Link to="/settings" onClick={onClose} className="underline font-medium">Settings → Email (SMTP)</Link> to enable sending emails.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 max-h-24 overflow-y-auto border border-gray-100 rounded-lg divide-y">
              {targets.map((t, i) => (
                <div key={i} className={`px-3 py-1.5 text-xs flex items-center gap-2 ${t.email ? '' : 'opacity-40'}`}>
                  <span className="font-medium text-gray-700 flex-1">{t.name || t.client_name}</span>
                  <span className="text-gray-400">{t.email || 'No email'}</span>
                </div>
              ))}
            </div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input ref={subjectRef} value={subject} onChange={e => setSubject(e.target.value)} className="input-field text-sm w-full mb-3" />
            <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} className="input-field text-sm w-full resize-y mb-4" />
            <div className="flex gap-3">
              <button onClick={send} disabled={sending || configured === null || withEmail.length === 0}
                className="flex-1 py-2 rounded-lg font-medium text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 flex items-center justify-center gap-2">
                <Mail size={14} /> {sending ? 'Sending…' : `Send to ${withEmail.length}`}
              </button>
              <button onClick={onClose} className="flex-1 py-2 rounded-lg font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
